export type UserNoticeType = "modal" | "banner";

export type UserNotice = {
  /** Keys the acknowledgement: change it to re-show the notice to everyone. */
  id: string;
  title: string;
  message: string;
  /** How it is presented; unknown or missing values fall back to a modal. */
  type: UserNoticeType;
  /** Not shown before this instant; null = no lower bound. */
  startDateTime: Date | null;
  /** Not shown from this instant on; null = no upper bound. */
  endDateTime: Date | null;
};

export const DEFAULT_USER_NOTICE_TITLE = "Notice";

const STORAGE_KEY_PREFIX = "userNoticeAck:";

const nonEmpty = (value: unknown): string | null =>
  typeof value === "string" && value.trim() ? value.trim() : null;

const parseType = (value: unknown): UserNoticeType =>
  value === "banner" ? "banner" : "modal";

/**
 * Reads an optional date-time bound. Returns `undefined` when the field is
 * absent or blank, `null` when it is present but not a string `Date` can
 * parse (ISO 8601 is the expected form, e.g. "2026-10-05T08:00:00Z"). A
 * non-string such as an epoch number is rejected rather than ignored, so a
 * bound ops meant to set never silently becomes "no bound".
 */
const parseDateTime = (value: unknown): Date | null | undefined => {
  if (value === undefined || value === null) return undefined;
  if (typeof value !== "string") return null;
  const raw = nonEmpty(value);
  if (raw === null) return undefined;
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
};

type UserNoticeWindow = Pick<UserNotice, "startDateTime" | "endDateTime">;

/**
 * Reads the display window from the flag object. Returns null when either
 * bound is malformed or the window ends at or before its start; a missing
 * bound is open (null on the result).
 */
const parseWindow = (obj: Record<string, unknown>): UserNoticeWindow | null => {
  const start = parseDateTime(obj.startDateTime);
  const end = parseDateTime(obj.endDateTime);
  if (start === null || end === null) return null;
  if (start && end && end <= start) return null;
  return { startDateTime: start ?? null, endDateTime: end ?? null };
};

/**
 * Parses the `user_notice` flag value. Accepts either a JSON object
 * `{ id?, title?, message, type?, startDateTime?, endDateTime? }` or a plain
 * message string. Returns null for anything without a usable message, with an
 * unparseable date, or with a window that ends before it starts, so a
 * malformed value shows nothing rather than an empty dialog or a notice that
 * never goes away.
 *
 * Without an explicit id the message text keys the acknowledgement, so
 * editing the text re-shows the notice.
 */
export const parseUserNotice = (value: unknown): UserNotice | null => {
  const raw = nonEmpty(value);
  if (!raw) return null;

  let parsed: unknown = raw;
  try {
    parsed = JSON.parse(raw);
  } catch {
    // Plain string value: use it as the message.
  }

  if (typeof parsed === "object" && parsed !== null) {
    const obj = parsed as Record<string, unknown>;
    const message = nonEmpty(obj.message);
    if (!message) return null;

    const window = parseWindow(obj);
    if (!window) return null;

    return {
      id: nonEmpty(obj.id) ?? message,
      title: nonEmpty(obj.title) ?? DEFAULT_USER_NOTICE_TITLE,
      message,
      type: parseType(obj.type),
      ...window
    };
  }

  // JSON that is not an object (a number, `true`, a quoted string) is still
  // shown verbatim; ops typing a bare sentence is the common case.
  return {
    id: raw,
    title: DEFAULT_USER_NOTICE_TITLE,
    message: raw,
    type: "modal",
    startDateTime: null,
    endDateTime: null
  };
};

/**
 * Whether `now` falls inside the notice's display window. The start is
 * inclusive and the end exclusive; a missing bound is open.
 */
export const isUserNoticeActive = (
  notice: Pick<UserNotice, "startDateTime" | "endDateTime">,
  now: number = Date.now()
): boolean =>
  (!notice.startDateTime || now >= notice.startDateTime.getTime()) &&
  (!notice.endDateTime || now < notice.endDateTime.getTime());

/**
 * Milliseconds until the window next opens or closes, or null when nothing
 * ahead will change `isUserNoticeActive` (no bounds, or already past the end).
 */
export const msUntilUserNoticeWindowChange = (
  notice: Pick<UserNotice, "startDateTime" | "endDateTime">,
  now: number = Date.now()
): number | null => {
  const start = notice.startDateTime?.getTime();
  const end = notice.endDateTime?.getTime();
  if (start !== undefined && now < start) return start - now;
  if (end !== undefined && now < end) return end - now;
  return null;
};

// Acknowledgement lives only in localStorage, keyed by notice id: it survives
// reloads and tabs, and comes back only when ops publish a notice with a new id.
export const isUserNoticeAcknowledged = (id: string): boolean => {
  try {
    return window.localStorage.getItem(STORAGE_KEY_PREFIX + id) !== null;
  } catch {
    return false;
  }
};

export const acknowledgeUserNotice = (id: string): void => {
  try {
    window.localStorage.setItem(
      STORAGE_KEY_PREFIX + id,
      new Date().toISOString()
    );
  } catch {
    // Storage unavailable (private mode, quota): the notice will show again
    // next load, which is the safe failure.
  }
};
